import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useGetCompaniesBySearchParamQuery, useGetDefaultCompanySuggestionsQuery, } from '@massilia/queries';
import { AutoComplete, AutocompleteOptionRow, Button, cn, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, FullScreenOverlay, Skeleton, useDebounce, useIsMobile, } from '@massilia/ui';
import { useStore } from '@tanstack/react-form';
import { Building, Check } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useFieldContext } from '../hooks/use-app-form.js';
import { DEFAULT_OPTION } from '../schemas/estimate/base-schemas.js';
import { FieldError } from './field-error.js';
export function CompanyAutocompleteField({ label, placeholder = 'Compagnie aérienne', valueField = 'id', }) {
    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebounce(searchText, 200);
    const isEmptySearch = debouncedSearchText.trim().length === 0;
    const { data: searchedOptions, isFetching: isSearching } = useGetCompaniesBySearchParamQuery({
        searchParam: debouncedSearchText || '',
    });
    // Recherche vide : la liste des compagnies les plus vendues, sinon le champ
    // s'ouvre sur rien — invisible sur écran, impasse dans un panneau plein écran.
    const { data: defaultSuggestions, isPending: isLoadingDefaults } = useGetDefaultCompanySuggestionsQuery();
    const options = useMemo(() => {
        const source = isEmptySearch ? defaultSuggestions : searchedOptions;
        if (!source)
            return [];
        return source.companies.map((company) => ({
            icon: _jsx(Building, { className: "size-4 text-gray-500" }),
            key: `${company.iataCode}-${company.name}`,
            label: `${company.iataCode} - ${company.name}`,
            value: company[valueField],
        }));
    }, [defaultSuggestions, isEmptySearch, searchedOptions, valueField]);
    const isLoadingOptions = isEmptySearch ? isLoadingDefaults : isSearching;
    const field = useFieldContext();
    const errors = useStore(field.store, (state) => state.meta.errors);
    const isMobile = useIsMobile();
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    const selectedOption = useMemo(() => {
        const currentValue = field.state.value?.value;
        if (!currentValue)
            return;
        const foundOption = options.find((opt) => opt.value === currentValue);
        if (foundOption)
            return foundOption;
        if (field.state.value?.label) {
            return field.state.value;
        }
        return;
    }, [options, field.state.value]);
    const triggerIcon = _jsx(Building, { className: "h-4 w-4 shrink-0 text-gray-light-700" });
    // Mobile : panneau plein écran, comme le champ aéroport. Un menu déroulant est
    // recouvert par le clavier dès qu'on tape, et la liste tient alors sur deux
    // lignes visibles.
    if (isMobile) {
        const handleSelect = (option) => {
            field.handleChange({ label: option.label, value: option.value });
            setSearchText('');
            setIsOverlayOpen(false);
        };
        return (_jsxs("div", { className: "relative flex flex-col gap-2", children: [label && _jsx("label", { className: "text-sm font-medium", children: label }), _jsxs(Button, { "aria-invalid": errors.length > 0, className: cn('data-[empty=true]:text-muted-foreground relative w-full justify-start gap-2 p-4 text-left font-normal', 'aria-invalid:ring-destructive/20 aria-invalid:border-destructive'), "data-empty": !selectedOption, onClick: () => setIsOverlayOpen(true), type: "button", variant: "outline", children: [triggerIcon, _jsx("span", { className: "truncate text-gray-light-600", children: selectedOption?.label ?? placeholder })] }), _jsx(FieldError, { errors: errors }), _jsx(FullScreenOverlay, { onOpenAutoFocus: (event) => {
                        // Le champ de recherche prend le focus lui-même : la portée de focus
                        // de Radix s'appuie sur `document.activeElement`, que la frontière du
                        // shadow DOM du widget WordPress lui cache.
                        event.preventDefault();
                        event.currentTarget
                            ?.querySelector('[data-slot="command-input"]')
                            // `preventScroll` n'est pas optionnel : au montage, le panneau est
                            // encore décalé d'un écran par son animation d'entrée, et un
                            // `focus()` nu ferait défiler la page pour l'y amener.
                            ?.focus({ preventScroll: true });
                    }, onOpenChange: (open) => {
                        setIsOverlayOpen(open);
                        if (!open) {
                            // Rouvrir le champ repart d'une recherche vide → suggestions par défaut.
                            setSearchText('');
                            field.handleBlur();
                        }
                    }, open: isOverlayOpen, title: placeholder, children: _jsxs(Command, { className: "flex h-full flex-col rounded-none", shouldFilter: false, children: [_jsx("div", { className: "border-b px-4 py-3", children: _jsx(CommandInput, { icon: triggerIcon, onValueChange: setSearchText, placeholder: placeholder, value: searchText }) }), _jsxs(CommandList, { className: "max-h-none flex-1 px-2 py-2", children: [options.length > 0 && (_jsx(CommandGroup, { children: options.map((option) => {
                                            const isSelected = selectedOption?.value === option.value;
                                            return (_jsx(CommandItem, { className: "flex w-full cursor-pointer items-center gap-3 px-2 py-3 active:bg-brand-50/60 data-[selected=true]:bg-brand-50/60", onSelect: () => handleSelect(option), value: option.key ?? option.label, children: _jsx(AutocompleteOptionRow, { iconSlotClassName: "w-5", option: option, trailingSlot: isSelected ? (_jsx(Check, { className: "h-4 w-4 shrink-0 text-brand-600" })) : null }) }, option.key ?? option.value));
                                        }) })), options.length === 0 && isLoadingOptions && (_jsxs("div", { className: "flex flex-col gap-2 px-2 py-2", children: [_jsx(Skeleton, { className: "h-10 w-full" }), _jsx(Skeleton, { className: "h-10 w-full" }), _jsx(Skeleton, { className: "h-10 w-full" })] })), options.length === 0 && !isLoadingOptions && (_jsx(CommandEmpty, { children: "Aucune compagnie trouv\u00E9e" }))] })] }) })] }));
    }
    return (_jsxs("div", { className: "relative flex flex-col gap-2", children: [label && _jsx("label", { className: "text-sm font-medium", children: label }), _jsx(AutoComplete, { emptyMessage: "Aucune compagnie trouv\u00E9e", icon: _jsx(Building, { className: "h-4 w-4 text-gray-light-700" }), isInvalid: errors.length > 0, isLoading: isLoadingOptions && options.length === 0, onBlurCallBack: () => {
                    field.handleBlur();
                }, onInputChange: (text) => {
                    setSearchText(text);
                }, onValueChange: (opt) => {
                    if (opt) {
                        field.handleChange({ label: opt.label, value: opt.value });
                    }
                    else {
                        field.handleChange(DEFAULT_OPTION);
                    }
                }, options: options, placeholder: placeholder, shouldFilter: false, value: selectedOption }), _jsx(FieldError, { errors: errors })] }));
}
